import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { Tag } from "component/shared/tag";
import { FormSelectField } from "component/shared/form-fields";
import { FollowUpBossAgentSelection } from "model/follow-up-boss-agent-selection";
import {
    AgentSelectionState,
    NO_SELECTION_LOCKED_MESSAGE,
    NO_USERS_SELECTED_WARNING,
    SELECTION_LOCK_WARNING,
    SELECTION_LOCKED_MESSAGE,
    SYNC_NOW_HINT,
    agentFor,
    availableAgents,
    canSelect,
    capStatus,
    deselectUser,
    isSelected,
    lockedPairs,
    selectUser,
    setAgent,
} from "./agent-selection";
import { followUpBossCopy } from "../../follow-up-boss-copy";

interface Props {
    selection: FollowUpBossAgentSelection | null;
    state: AgentSelectionState | null;
    loading: boolean;
    // GET failure (FUB unreachable) or the last save's rejection; rendered in the table slot
    error: string | null;
    onChange: (state: AgentSelectionState) => void;
    onRetry: () => void;
    disabled?: boolean;
    // MAX-4579: a confirmed selection is locked; render the saved pairs as text with the
    // contact-support note instead of the editable table
    readOnly?: boolean;
    // Settings tab only: the saved selection applies to existing contacts on the next Sync Now (R5)
    showSyncHint?: boolean;
    // Wizard only: rendered under the load error so Continue without a selection is explained
    unavailableNote?: string;
    // Warn when nothing is selected, since an empty selection imports nothing and the save locks it
    showEmptyWarning?: boolean;
    // MAX-4580: reseller accounts see the white-labeled wording. Required rather than optional:
    // a brand gate that defaults to the branded copy fails open, which is the exact leak this
    // ticket exists to prevent.
    whiteLabel: boolean;
}

const warningBoxStyle: React.CSSProperties = { gap: 8, backgroundColor: "#FFF9E0", borderRadius: 4, padding: 16, marginTop: 16 };

/**
 * MAX-4236 agent selection table, shared by wizard step 2 and the Settings tab. One row per
 * eligible Follow Up Boss user with a checkbox and an agent picker, and a seat-cap
 * counter. MAX-4579: no unassigned/pond toggle (those contacts are never imported), a warning
 * that the choice locks, and a read-only rendering once it has. Renders only: every decision
 * lives in agent-selection.ts. All copy is engineering placeholder (P7).
 */
export function AgentSelectionTable({ selection, state, loading, error, onChange, onRetry, disabled, readOnly, showSyncHint, unavailableNote, showEmptyWarning, whiteLabel }: Props) {
    const copy = followUpBossCopy(whiteLabel);
    if (loading) {
        return (
            <Mui.Box display="flex" alignItems="center" style={{ gap: 12, padding: "16px 0" }}>
                <Mui.CircularProgress size={18} />
                <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>Loading Follow Up Boss users…</Mui.Typography>
            </Mui.Box>
        );
    }
    if (!selection || !state) {
        return (
            <Mui.Box style={{ padding: "8px 0" }}>
                <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Icons.XCircle size={16} style={{ color: "#EF4444", flexShrink: 0 }} />
                    <Mui.Typography style={{ fontSize: 16, lineHeight: "24px", color: "#5F6368" }}>
                        {error ?? "Could not load Follow Up Boss users."}
                    </Mui.Typography>
                    <Mui.Link component="button" variant="body2" onClick={onRetry} style={{ fontWeight: 600 }}>
                        Retry
                    </Mui.Link>
                </Mui.Box>
                {unavailableNote && (
                    <Mui.Typography variant="body2" style={{ color: "#5F6368", marginTop: 8 }}>{unavailableNote}</Mui.Typography>
                )}
            </Mui.Box>
        );
    }

    if (readOnly) {
        const pairs = lockedPairs(selection);
        return (
            <Mui.Box>
                <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                    <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#1C1B1F" }}>Follow Up Boss users</Mui.Typography>
                    <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>
                        {capStatus({ rows: pairs.map((p) => ({ fubUserId: p.fubUserId, agentId: p.agentId })) }, selection.seatCap).label}
                    </Mui.Typography>
                </Mui.Box>
                {pairs.length === 0 ? (
                    <Mui.Typography variant="body2" style={{ color: "#5F6368", padding: "8px 0" }}>
                        {NO_SELECTION_LOCKED_MESSAGE}
                    </Mui.Typography>
                ) : (
                    <>
                        <Mui.Table size="small">
                            <Mui.TableBody>
                                {pairs.map((pair) => (
                                    <Mui.TableRow key={pair.fubUserId}>
                                        <Mui.TableCell style={{ verticalAlign: "top" }}>
                                            <Mui.Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
                                                <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#1C1B1F" }}>{pair.userName}</Mui.Typography>
                                                {pair.isOwner && <Tag label="Account owner" variant="info" size="small" />}
                                            </Mui.Box>
                                            {pair.userEmail && (
                                                <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{pair.userEmail}</Mui.Typography>
                                            )}
                                        </Mui.TableCell>
                                        <Mui.TableCell style={{ verticalAlign: "top", width: "45%" }}>
                                            <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{copy.agentColumnLabel}</Mui.Typography>
                                            <Mui.Typography variant="body2" style={{ color: "#1C1B1F" }}>{pair.agentName}</Mui.Typography>
                                        </Mui.TableCell>
                                    </Mui.TableRow>
                                ))}
                            </Mui.TableBody>
                        </Mui.Table>
                        <Mui.Box display="flex" style={{ gap: 8, marginTop: 16 }}>
                            <Icons.Lock size={16} style={{ color: "#5F6368", flexShrink: 0, marginTop: 2 }} />
                            <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{SELECTION_LOCKED_MESSAGE}</Mui.Typography>
                        </Mui.Box>
                    </>
                )}
                {showSyncHint && (
                    <Mui.Typography variant="body2" style={{ color: "#5F6368", marginTop: 16 }}>
                        {SYNC_NOW_HINT}
                    </Mui.Typography>
                )}
            </Mui.Box>
        );
    }

    const cap = capStatus(state, selection.seatCap);

    return (
        <Mui.Box>
            <Mui.Typography variant="body2" style={{ color: "#1C1B1F", marginBottom: 16 }}>
                {copy.selectionIntro}
            </Mui.Typography>

            <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#1C1B1F" }}>Follow Up Boss users</Mui.Typography>
                <Mui.Typography variant="body2" style={{ color: cap.atCap && selection.seatCap !== null ? "#9D6506" : "#5F6368" }}>
                    {cap.label}
                </Mui.Typography>
            </Mui.Box>

            {selection.fubUsers.length === 0 ? (
                <Mui.Typography variant="body2" style={{ color: "#5F6368", padding: "8px 0" }}>
                    No lead-owning users were found on your Follow Up Boss account.
                </Mui.Typography>
            ) : (
                <Mui.Table size="small">
                    <Mui.TableBody>
                        {selection.fubUsers.map((user) => {
                            const selected = isSelected(state, user.id);
                            const selectable = canSelect(state, selection.seatCap, user.id);
                            return (
                                <Mui.TableRow key={user.id}>
                                    <Mui.TableCell padding="checkbox" style={{ verticalAlign: "top", paddingTop: 12 }}>
                                        <Mui.Checkbox
                                            color="secondary"
                                            size="small"
                                            checked={selected}
                                            disabled={disabled || !selectable}
                                            onChange={(e) => onChange(e.target.checked
                                                ? selectUser(state, selection.seatCap, user.id)
                                                : deselectUser(state, user.id))}
                                        />
                                    </Mui.TableCell>
                                    <Mui.TableCell style={{ verticalAlign: "top", paddingTop: 16 }}>
                                        <Mui.Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
                                            <Mui.Typography variant="body2" style={{ fontWeight: 600, color: selectable || selected ? "#1C1B1F" : "#5F6368" }}>
                                                {user.name}
                                            </Mui.Typography>
                                            {user.isOwner && <Tag label="Account owner" variant="info" size="small" />}
                                        </Mui.Box>
                                        {user.email && (
                                            <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{user.email}</Mui.Typography>
                                        )}
                                    </Mui.TableCell>
                                    <Mui.TableCell style={{ verticalAlign: "top", width: "45%" }}>
                                        {selected && (
                                            <FormSelectField
                                                label={copy.agentColumnLabel}
                                                value={agentFor(state, user.id) ?? ""}
                                                onChange={(value) => onChange(setAgent(state, user.id, value === "" ? null : Number(value)))}
                                                options={availableAgents(selection, state, user.id)}
                                                valueExtractor={(a) => a.id}
                                                labelExtractor={(a) => a.name || a.email || `Agent ${a.id}`}
                                                error={agentFor(state, user.id) === null}
                                                disabled={disabled}
                                            />
                                        )}
                                    </Mui.TableCell>
                                </Mui.TableRow>
                            );
                        })}
                    </Mui.TableBody>
                </Mui.Table>
            )}

            {showEmptyWarning && selection.fubUsers.length > 0 && state.rows.length === 0 && (
                <Mui.Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 12 }}>
                    <Icons.AlertTriangle size={16} style={{ color: "#9D6506", flexShrink: 0 }} />
                    <Mui.Typography variant="body2" style={{ color: "#9D6506" }}>{NO_USERS_SELECTED_WARNING}</Mui.Typography>
                </Mui.Box>
            )}

            {/* MAX-4579: the choice locks on save (design-system warning banner, as auto-create-toggle.tsx) */}
            <Mui.Box display="flex" style={warningBoxStyle}>
                <Icons.AlertTriangle size={16} style={{ color: "#9D6506", flexShrink: 0, marginTop: 4 }} />
                <Mui.Typography style={{ fontSize: 16, lineHeight: "24px", color: "#1C1B1F" }}>{SELECTION_LOCK_WARNING}</Mui.Typography>
            </Mui.Box>

            {error && (
                <Mui.Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 16 }}>
                    <Icons.XCircle size={16} style={{ color: "#EF4444", flexShrink: 0 }} />
                    <Mui.Typography style={{ fontSize: 16, lineHeight: "24px", color: "#5F6368" }}>{error}</Mui.Typography>
                </Mui.Box>
            )}

            {showSyncHint && (
                <Mui.Typography variant="body2" style={{ color: "#5F6368", marginTop: 16 }}>
                    {SYNC_NOW_HINT}
                </Mui.Typography>
            )}
        </Mui.Box>
    );
}
